import React, { useState, useCallback } from 'react';
import { Button, Popconfirm, Modal, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FJ191, FJ192 } from '../../../services/j208Api';
import LookupField from '../../../components/LookupField';

interface Tab2Props {
  details: Partial<FJ191>[];
  isEditing: boolean;
  onAdd: () => void;
  onDelete: (idx: number) => void;
  onDetailChange: (idx: number, key: keyof FJ191, value: FJ191[keyof FJ191]) => void;
  onBoardNumbersChange: (detailIdx: number, boardNumbers: Partial<FJ192>[]) => void;
  selectedDetailIdx: number;
  onSelectDetail: (idx: number) => void;
  totalItems?: number;
}

const Tab2Details: React.FC<Tab2Props> = ({
  details, isEditing, onAdd, onDelete, onDetailChange, onBoardNumbersChange,
  selectedDetailIdx, onSelectDetail, totalItems,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [activeDetailIdx, setActiveDetailIdx] = useState(-1);
  const [editingBoardNumbers, setEditingBoardNumbers] = useState<Partial<FJ192>[]>([]);

  const openBoardModal = useCallback((idx: number) => {
    setActiveDetailIdx(idx);
    const bns = details[idx]?.board_numbers || [];
    setEditingBoardNumbers(bns.map(bn => ({ ...bn })));
    setModalVisible(true);
  }, [details]);

  const saveBoardNumbers = useCallback(() => {
    if (activeDetailIdx >= 0) {
      onBoardNumbersChange(activeDetailIdx, editingBoardNumbers);
    }
    setModalVisible(false);
  }, [activeDetailIdx, editingBoardNumbers, onBoardNumbersChange]);

  const addBoardRow = useCallback(() => {
    setEditingBoardNumbers(prev => [
      ...prev,
      { fj192_03: prev.length + 1, fj192_11: '' },
    ]);
  }, []);

  const deleteBoardRow = useCallback((idx: number) => {
    setEditingBoardNumbers(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBoardField = useCallback((idx: number, value: string) => {
    setEditingBoardNumbers(prev => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], fj192_11: value };
      }
      return next;
    });
  }, []);

  const sel = selectedDetailIdx >= 0 && selectedDetailIdx < details.length
    ? details[selectedDetailIdx]
    : null;

  return (
    <div className="j208-tab2">
      {/* Grid area */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>繳庫明細 ({details.length} 筆)
          {totalItems !== undefined && <span style={{ marginLeft: 12, fontWeight: 400, fontSize: 12, color: '#999' }}>總項次: {totalItems}</span>}
        </span>
        {isEditing && (
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={onAdd}>
            新增明細
          </Button>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="detail-table">
          <thead>
            <tr>
              <th style={{ width: 35 }} data-contract-field="fj191_02">項次</th>
              <th style={{ width: 84 }} data-contract-field="fj191_10">產品大類</th>
              <th style={{ width: 84 }} data-contract-field="fj191_11">產品細類</th>
              <th style={{ width: 55 }} data-contract-field="fj191_12">色號</th>
              <th style={{ width: 37 }} data-contract-field="fj191_17">等級</th>
              <th style={{ width: 85 }} data-contract-field="fj191_13">繳庫數量</th>
              <th style={{ width: 44 }} data-contract-field="fj191_14">單位</th>
              <th style={{ width: 63 }} data-contract-field="fj191_26">架號</th>
              <th style={{ width: 80 }} data-contract-field="fj191_18">換算率</th>
              <th style={{ width: 69 }} data-contract-field="fj191_151">含稅單價</th>
              <th style={{ width: 69 }} data-contract-field="fj191_15">未稅單價</th>
              <th style={{ width: 79 }} data-contract-field="fj191_161">含稅金額</th>
              <th style={{ width: 89 }} data-contract-field="fj191_16">未稅金額</th>
              <th style={{ width: 50 }} data-contract-field="fj192_11">板號</th>
              {isEditing && <th style={{ width: 50 }} data-acceptance-non-field="row_actions">操作</th>}
            </tr>
          </thead>
          <tbody>
            {details.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 15 : 14} style={{ textAlign: 'center', color: '#999', padding: 24 }}>
                  尚無明細資料
                </td>
              </tr>
            )}
            {details.map((d, idx) => (
              <tr
                key={idx}
                className={selectedDetailIdx === idx ? 'row-selected' : ''}
                onClick={() => onSelectDetail(idx)}
                style={{ cursor: 'pointer' }}
              >
                <td>{d.fj191_02 ?? idx + 1}</td>
                <td>{d.fj191_10 || ''}</td>
                <td>{d.fj191_11 || ''}</td>
                <td>{d.fj191_12 || ''}</td>
                <td>
                  <input type="text" maxLength={2} disabled={!isEditing}
                    value={d.fj191_17 || ''}
                    onChange={e => onDetailChange(idx, 'fj191_17', e.target.value)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <InputNumber step={0.01} disabled={!isEditing}
                    value={d.fj191_13 ?? 0}
                    onChange={v => onDetailChange(idx, 'fj191_13', v || 0)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <input type="text" maxLength={4} disabled={!isEditing}
                    value={d.fj191_14 || ''}
                    onChange={e => onDetailChange(idx, 'fj191_14', e.target.value)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <input type="text" maxLength={6} disabled={!isEditing}
                    value={d.fj191_26 || ''}
                    onChange={e => onDetailChange(idx, 'fj191_26', e.target.value)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <InputNumber step={0.0001} disabled={!isEditing}
                    value={d.fj191_18 ?? 0}
                    onChange={v => onDetailChange(idx, 'fj191_18', v || 0)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <InputNumber step={0.001} disabled={!isEditing}
                    value={d.fj191_151 ?? 0}
                    onChange={v => onDetailChange(idx, 'fj191_151', v || 0)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <InputNumber step={0.001} disabled={!isEditing}
                    value={d.fj191_15 ?? 0}
                    onChange={v => onDetailChange(idx, 'fj191_15', v || 0)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <InputNumber step={0.001} disabled={!isEditing}
                    value={d.fj191_161 ?? 0}
                    onChange={v => onDetailChange(idx, 'fj191_161', v || 0)}
                    style={{ width: '100%' }} />
                </td>
                <td>
                  <InputNumber step={0.01} disabled={!isEditing}
                    value={d.fj191_16 ?? 0}
                    onChange={v => onDetailChange(idx, 'fj191_16', v || 0)}
                    style={{ width: '100%' }} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Button type="link" size="small" icon={<EditOutlined />}
                    onClick={() => openBoardModal(idx)}>
                    {d.board_numbers?.length || 0}
                  </Button>
                </td>
                {isEditing && (
                  <td>
                    <Popconfirm title="確定刪除此明細?" onConfirm={() => onDelete(idx)}>
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail fields area (below grid, for selected row) */}
      {sel && (
        <div className="form-section" style={{ marginTop: 16 }}>
          <span className="section-tag">明細第 {selectedDetailIdx + 1} 行 — 詳細資料</span>
          <div className="form-grid">
            <label>總項次</label>
            <input type="text" readOnly value={String(totalItems ?? 0)} style={{ maxWidth: 40 }} />

            <label>CBNO.</label>
            <LookupField
              lookupType="colorBatch"
              maxLength={10}
              disabled={!isEditing}
              value={sel.fj191_12a || ''}
              onChange={(code) => onDetailChange(selectedDetailIdx, 'fj191_12a', code)}
              onCodeConfirm={(code) => onDetailChange(selectedDetailIdx, 'fj191_12a', code)}
              style={{ maxWidth: 120 }}
            />

            <label>非A級品原因</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 300 }}>
              <LookupField
                lookupType="grade"
                disabled={!isEditing}
                value=""
                onChange={() => {}}
                onCodeConfirm={(code) => {
                  if (code) onDetailChange(selectedDetailIdx, 'fj191_171', code);
                }}
                style={{ maxWidth: 300 }}
              />
              <textarea
                disabled={!isEditing}
                value={sel.fj191_171 || ''}
                onChange={e => onDetailChange(selectedDetailIdx, 'fj191_171', e.target.value)}
                rows={2}
                style={{ maxWidth: 300 }}
              />
            </div>

            <label>產品名稱</label>
            <textarea
              disabled={!isEditing}
              value={sel.fj191_19 || ''}
              onChange={e => onDetailChange(selectedDetailIdx, 'fj191_19', e.target.value)}
              rows={2}
              style={{ maxWidth: 300 }} />

            <label>色號</label>
            <LookupField
              lookupType="color"
              maxLength={3}
              disabled={!isEditing}
              value={sel.fj191_12 || ''}
              onChange={(code) => onDetailChange(selectedDetailIdx, 'fj191_12', code)}
              onCodeConfirm={(code) => onDetailChange(selectedDetailIdx, 'fj191_12', code)}
              style={{ maxWidth: 60 }}
            />

            <label>中文色名</label>
            <input type="text" readOnly value={sel.fj191_121 || ''} style={{ maxWidth: 160 }} />

            <label>產品規格</label>
            <textarea
              disabled={!isEditing}
              value={sel.fj191_20 || ''}
              onChange={e => onDetailChange(selectedDetailIdx, 'fj191_20', e.target.value)}
              rows={2}
              style={{ maxWidth: 300 }} />

            <label>英文色名</label>
            <input type="text" readOnly value={sel.fj191_122 || ''} style={{ maxWidth: 160 }} />

            <label>加工別</label>
            <textarea
              disabled={!isEditing}
              value={sel.fj191_21 || ''}
              onChange={e => onDetailChange(selectedDetailIdx, 'fj191_21', e.target.value)}
              rows={2}
              style={{ maxWidth: 300 }} />

            <label>針孔內</label>
            <InputNumber step={0.1} disabled={!isEditing}
              value={sel.fj191_22 ?? 0}
              onChange={v => onDetailChange(selectedDetailIdx, 'fj191_22', v || 0)}
              style={{ maxWidth: 100 }} />

            <label>碼重</label>
            <InputNumber step={0.1} disabled={!isEditing}
              value={sel.fj191_24 ?? 0}
              onChange={v => onDetailChange(selectedDetailIdx, 'fj191_24', v || 0)}
              style={{ maxWidth: 100 }} />

            <label>針孔外</label>
            <InputNumber step={0.1} disabled={!isEditing}
              value={sel.fj191_23 ?? 0}
              onChange={v => onDetailChange(selectedDetailIdx, 'fj191_23', v || 0)}
              style={{ maxWidth: 100 }} />

            <label>米平方重</label>
            <InputNumber step={0.1} disabled={!isEditing}
              value={sel.fj191_25 ?? 0}
              onChange={v => onDetailChange(selectedDetailIdx, 'fj191_25', v || 0)}
              style={{ maxWidth: 100 }} />
          </div>
        </div>
      )}

      {/* Board Number Modal (FJ192) */}
      <Modal
        title={`板號明細 — 繳庫明細第 ${activeDetailIdx + 1} 行`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={isEditing ? saveBoardNumbers : () => setModalVisible(false)}
        okText={isEditing ? '確定' : '關閉'}
        cancelButtonProps={{ style: isEditing ? {} : { display: 'none' } }}
        width={500}
      >
        {isEditing && (
          <div style={{ marginBottom: 8, textAlign: 'right' }}>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addBoardRow}>
              新增板號
            </Button>
          </div>
        )}
        <table className="detail-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>板號</th>
              {isEditing && <th style={{ width: 50 }}>操作</th>}
            </tr>
          </thead>
          <tbody>
            {editingBoardNumbers.length === 0 && (
              <tr>
                <td colSpan={isEditing ? 3 : 2} style={{ textAlign: 'center', color: '#999', padding: 16 }}>
                  尚無板號資料
                </td>
              </tr>
            )}
            {editingBoardNumbers.map((bn, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>
                  <input type="text" maxLength={20} disabled={!isEditing}
                    value={bn.fj192_11 || ''}
                    onChange={e => updateBoardField(idx, e.target.value)}
                    style={{ width: '100%' }} />
                </td>
                {isEditing && (
                  <td>
                    <Popconfirm title="刪除此板號?" onConfirm={() => deleteBoardRow(idx)}>
                      <Button type="link" danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Modal>
    </div>
  );
};

export default Tab2Details;
